import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { SiteImagesProvider } from "@/context/SiteImagesContext";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/ui-bits";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PublicSearch from "@/pages/PublicSearch";
import ArtistDashboard from "@/pages/ArtistDashboard";
import CreateSearch from "@/pages/CreateSearch";
import BeatmakerFeed from "@/pages/BeatmakerFeed";
import MyProductions from "@/pages/MyProductions";
import SearchRouter from "@/pages/SearchRouter";
import Messages from "@/pages/Messages";
import Conversation from "@/pages/Conversation";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import BeatmakerProfile from "@/pages/BeatmakerProfile";
import Admin from "@/pages/Admin";
import VerifyEmail from "@/pages/VerifyEmail";
import StartArtist from "@/pages/StartArtist";
import StartBeatmaker from "@/pages/StartBeatmaker";

function homeFor(user) {
  if (user?.role === "beatmaker") return "/app/feed";
  if (user?.role === "admin") return "/app/admin";
  return "/app";
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return <AppShell>{children}</AppShell>;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "beatmaker") return <Navigate to="/app/feed" replace />;
  if (user?.role === "admin") return <Navigate to="/app/admin" replace />;
  return <ArtistDashboard />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SiteImagesProvider>
        <PlayerProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-center" richColors />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/r/:code" element={<PublicSearch />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/start/artist" element={<StartArtist />} />
              <Route path="/start/beatmaker" element={<StartBeatmaker />} />

              <Route path="/app" element={<Protected><HomeRedirect /></Protected>} />
              <Route path="/app/create" element={<Protected roles={["artist"]}><CreateSearch /></Protected>} />
              <Route path="/app/feed" element={<Protected roles={["beatmaker"]}><BeatmakerFeed /></Protected>} />
              <Route path="/app/productions" element={<Protected roles={["beatmaker"]}><MyProductions /></Protected>} />
              <Route path="/app/search/:id" element={<Protected roles={["artist", "beatmaker"]}><SearchRouter /></Protected>} />
              <Route path="/app/messages" element={<Protected roles={["artist", "beatmaker"]}><Messages /></Protected>} />
              <Route path="/app/messages/:cid" element={<Protected roles={["artist", "beatmaker"]}><Conversation /></Protected>} />
              <Route path="/app/notifications" element={<Protected roles={["artist", "beatmaker"]}><Notifications /></Protected>} />
              <Route path="/app/profile" element={<Protected roles={["artist", "beatmaker"]}><Profile /></Protected>} />
              <Route path="/app/beatmaker/:id" element={<Protected roles={["artist", "beatmaker"]}><BeatmakerProfile /></Protected>} />
              <Route path="/app/admin" element={<Protected roles={["admin"]}><Admin /></Protected>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </PlayerProvider>
        </SiteImagesProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
